import { FluidTheme, FluidThemeInterface } from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Prop,
  State,
} from '@stencil/core';

import { Utils } from '../../utils/utils';

/**
 * @displayName Content Collapse
 */
@Component({
  tag: 'fluid-content-collapse',
  styleUrl: 'fluid-content-collapse.scss',
  shadow: true,
})
export class FluidContentCollapse
  implements ComponentInterface, FluidThemeInterface
{
  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidContentCollapseElement;

  // ================================================================ //
  // -- States

  @State() _dynamicHeight = 0;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Allows the user to setup the collapse state: true or false for collapse or expand
   */
  @Prop({ reflect: true }) isCollapsed: boolean;

  /**
   * Allows the user to set up the height when the element is collapsed, the height is in px
   */
  @Prop({ mutable: true }) collapsedHeight = 200;

  /**
   * Allows the user to set up the height when the element is expanded, the height is in px
   * If allowDynamicHeight property is set to `true` this value will be ignored
   */
  @Prop({ mutable: true }) maxContentHeight = 1000;

  /**
   * If allowDynamicHeight property is set to `true` the component automatically
   * will set the dynamic height based on their children's height.
   */
  @Prop({ mutable: true }) allowDynamicHeight = false;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
  }

  componentDidLoad() {
    const slot = this.host.shadowRoot.querySelector('slot') as HTMLSlotElement;
    if (this.allowDynamicHeight && slot) {
      this.setDynamicHeight(slot);
    }
  }

  // ================================================================ //
  // -- Local Methods

  setDynamicHeight(slot: HTMLSlotElement) {
    // Dynamic maxContentHeight
    if (!slot) return;

    const gap = 30;
    const children: Node[] = slot?.assignedNodes();

    // avoid extra re-renders setting an internal variable
    this._dynamicHeight = children.reduce((pre, cur) => {
      return (cur as HTMLDivElement).getBoundingClientRect().height + pre + gap;
    }, 0);
  }

  getHeight() {
    return this.allowDynamicHeight
      ? this._dynamicHeight
      : this.maxContentHeight;
  }

  getContainerClass() {
    const expandedHeight = this.getHeight();

    return this.isCollapsed
      ? {
          'max-height': `${this.collapsedHeight}px`,
        }
      : {
          'max-height': `${expandedHeight}px`,
        };
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <div class={`fluid-expand-container theme-${this.theme}`}>
        <div class='dynamic-content' style={this.getContainerClass()}>
          <slot />
        </div>
      </div>
    );
  }
}
